import authService from '@/services/zybook-auth-service';
import { connectToChild } from 'penpal';
import { useEffect, useRef, useState } from 'react';

interface ChildIframeConnection {
    loadSection: (data: { sectionResponse: any; zybooksResponse: any; testQuestions?: any }) => Promise<void>;
    loadAboutPages: (data: { zybooksResponse: any }) => Promise<void>;
    snapToIndex: (index: number, options?: { highlight?: boolean; flash?: boolean }) => Promise<void>;
    snapToGuid: (guid: string, options?: { highlight?: boolean; flash?: boolean }) => Promise<void>;
    getViewportTopResourceGuid?: () => Promise<string | null>;
    updateByIndex: (index: number, json: any) => Promise<void>;
    // Built-in methods
    destroy: () => Promise<void>;
}

interface UseLivePreviewProps {
    onSnapToIndex?: (index: number) => void;
    onNavigateToSection?: (href: string) => void;
}

function getLivePreviewIframeURL() {
    const baseZybooksURL = import.meta.env.VITE_ZYBOOKS_URL;
    const livePreviewPath = '/section-from-message';
    const refreshToken = authService.getRefreshToken();
    const refreshTokenParam = refreshToken ? `?refresh_token=${encodeURIComponent(refreshToken)}` : '';
    return `${baseZybooksURL}${livePreviewPath}${refreshTokenParam}`;
}

export function useLivePreview({ onSnapToIndex, onNavigateToSection }: UseLivePreviewProps) {
    const iframeURL = getLivePreviewIframeURL();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const connectionRef = useRef<ChildIframeConnection | null>(null);
    const [connection, setConnection] = useState<ChildIframeConnection | null>(null);
    const [isResettingConnection, setIsResettingConnection] = useState<boolean>(false);
    const [connectionError, setConnectionError] = useState<string | null>(null);

    const establishConnection = async () => {
        if (!iframeRef.current) {
            return;
        }
        setConnectionError(null);

        if (connectionRef.current) {
            await connectionRef.current.destroy();
            connectionRef.current = null;
            setConnection(null);
        }

        try {
            const connectionObj = connectToChild({
                iframe: iframeRef.current,
                methods: {
                    snapToIndex: (index: number) => {
                        console.log('Parent received snapToIndex:', index);
                        onSnapToIndex?.(index);
                    },
                    navigateToSection: (href: string) => {
                        onNavigateToSection?.(href);
                    },
                },
                timeout: 5000,
            });

            const childConnection = await connectionObj.promise;
            const nextConnection = {
                ...childConnection,
                destroy: connectionObj.destroy,
            } as ChildIframeConnection;
            connectionRef.current = nextConnection;
            setConnection(nextConnection);
        } catch (error) {
            console.error('Failed to establish connection:', error);
            setConnection(null);
            setConnectionError('Failed to establish connection with the iframe.');
        }
    };

    const destroyConnection = async () => {
        if (connectionRef.current) {
            await connectionRef.current.destroy();
            connectionRef.current = null;
            setConnection(null);
        }
    };

    const resetConnection = async () => {
        setIsResettingConnection(true);
        try {
            await destroyConnection();
            refreshPreviewIframe();
            await establishConnection();
        } finally {
            setIsResettingConnection(false);
        }
    };

    const refreshPreviewIframe = () => {
        if (iframeRef.current && iframeRef.current.contentWindow) {
            const src = iframeRef.current.src;
            iframeRef.current.src = '';
            iframeRef.current.src = src;
        }
    };

    const snapToIndex = (index: number) => {
        console.log('Snapping to index:', index);
        if (connection) {
            connection
                .snapToIndex(index, { highlight: true, flash: true })
                .then(() => console.log('Snapped to index:', index))
                .catch(error => console.error('Failed to snap to index:', error));
        }
    };

    const snapToGuid = (guid: string) => {
        if (connection) {
            connection.snapToGuid(guid).catch(error => console.error('Failed to snap to guid:', error));
        }
    };

    const getViewportTopResourceGuidFromPreview = async (): Promise<string | null> => {
        if (!connection?.getViewportTopResourceGuid) {
            return null;
        }
        try {
            return (await connection.getViewportTopResourceGuid()) ?? null;
        } catch (error) {
            console.error('Failed to get viewport top resource guid:', error);
            return null;
        }
    };

    const updateByIndex = (index: number, json: any) => {
        console.log('Sending update for index:', index, json);
        if (connection) {
            connection
                .updateByIndex(index, json)
                .then(() => console.log('Updated index:', index))
                .catch(error => console.error('Failed to update index:', error));
        }
    };

    useEffect(() => {
        console.log('Connecting to preview iframe...');
        void establishConnection();

        return () => {
            if (connectionRef.current) {
                void connectionRef.current.destroy();
                connectionRef.current = null;
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        iframeRef,
        iframeURL,
        connection,
        connectionError,
        isResettingConnection,
        resetConnection,
        destroyConnection,
        snapToIndex,
        snapToGuid,
        getViewportTopResourceGuidFromPreview,
        updateByIndex,
    };
}
